/**
 * O envio, do clique até a mensagem aparecer no histórico (H06, §11.1).
 *
 * ── Otimismo só DEPOIS de a API aceitar ───────────────────────────────────
 *
 * A §11.1 é precisa: *"envio de mensagem pode ser otimista apenas depois de a
 * API Core aceitar o comando; mostrar estado pendente"*.
 *
 * E a Armadilha diz o que acontece quando se antecipa: *"a API recusa por
 * janela do WhatsApp, o indicador já está verde, e o vendedor acha que
 * respondeu"*. Não é um pixel errado — é o vendedor deixando de responder um
 * cliente que está esperando.
 *
 * Então há três estados locais, e a diferença entre os dois primeiros é o
 * assunto desta task:
 *
 *   `pendente` — saiu daqui, o servidor ainda não respondeu. A mensagem
 *                aparece no histórico, e aparece DIFERENTE: esmaecida, sem
 *                carimbo de hora, com "enviando".
 *   `aceita`   — a API respondeu 201. Agora sim ela parece uma mensagem
 *                enviada, ainda que a projeção não tenha chegado.
 *   `falhou`   — recusada. O texto NÃO se perde, e a falha diz o motivo e o
 *                que fazer.
 *
 * ── A chave de idempotência sobrevive ao retry ────────────────────────────
 *
 * A Técnica é explícita: *"`Idempotency-Key` gerado no cliente e mantido no
 * retry. Chave nova a cada tentativa anula a proteção, e o duplo clique volta a
 * duplicar."*
 *
 * Por isso a chave nasce com o envio e não com a requisição. É o campo mais
 * importante deste arquivo, e o mais fácil de errar: gerar dentro da função que
 * chama a API pareceria natural e quebraria a garantia.
 */

import { ErroDaApi, ErroDeRede } from '@cliqcrm/contracts';

export type EstadoDoEnvio = 'pendente' | 'aceita' | 'falhou';

export interface EnvioLocal {
  /** Identidade na tela. Não é o canônico — este envio pode nunca ter um. */
  readonly id: string;
  /**
   * A conversa a que este envio pertence.
   *
   * ── Por que o envio precisa saber, em vez de a tela lembrar ─────────────
   *
   * A fila viveu sem isto e o efeito foi visível em 02/09/2026: um envio some
   * da tela quando pareia com uma mensagem projetada, e na conversa ERRADA ele
   * nunca pareia — então aparecia em todas as conversas abertas depois dele. A
   * mesma mensagem enviada para a Camilli desenhava na conversa do Gabriel, e
   * o contrário.
   *
   * Nada tinha sido enviado para a pessoa errada — mas quem olhava a tela não
   * tinha como saber disso, que é o pior tipo de defeito numa tela de
   * atendimento.
   *
   * E havia um segundo, este não cosmético: repetir um envio que falhou usava
   * a conversa ABERTA no momento do clique, e não a do envio. Trocar de
   * conversa antes de repetir mandaria a mensagem para outra pessoa de
   * verdade.
   */
  readonly conversaId: string;
  /**
   * A `Idempotency-Key`. Nasce com o envio e NÃO muda no retry — é o que
   * impede o duplo clique de virar duas mensagens para o cliente.
   */
  readonly chave: string;
  readonly conteudo: string;
  readonly privada: boolean;
  readonly templateNome: string | undefined;
  /** Um valor por variável do modelo, na ordem — só com modelo escolhido. */
  readonly templateValores: readonly string[] | undefined;
  /** Quem a nota interna chama com "@" (#569): ids de pessoas. */
  readonly mencoes: readonly string[];
  /**
   * Os arquivos, guardados no envio (H16).
   *
   * ── Por que aqui, e não no editor ───────────────────────────────────────
   *
   * Porque a falha precisa preservá-los. A H06 aprendeu isso com o texto —
   * *"falha que guarda o texto"* — e com arquivo é pior: perder um documento
   * que a pessoa acabou de buscar na pasta faz ela procurar de novo, e o
   * "procurar de novo" costuma ser num celular, num aplicativo de banco, num
   * e-mail antigo.
   *
   * Guardados no envio, o botão de repetir reenvia os mesmos arquivos sem
   * pedir nada.
   */
  readonly anexos: readonly File[];
  readonly estado: EstadoDoEnvio;
  readonly erro: unknown;
  /** Quando o usuário clicou. É o que ordena o envio no histórico. */
  readonly criadoEm: string;
  /** `criadaEm` da resposta, quando a API aceitou. */
  readonly aceitoEm: string | null;
}

function novoId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `envio-${Date.now()}-${Math.random()}`;
}

export function criarEnvio(
  conversaId: string,
  conteudo: string,
  privada: boolean,
  templateNome?: string,
  agora = new Date(),
  anexos: readonly File[] = [],
  /** Um valor por variável do modelo, na ordem. */
  templateValores?: readonly string[],
  mencoes: readonly string[] = [],
): EnvioLocal {
  return {
    id: novoId(),
    conversaId,
    // Uma chave por ENVIO, não por tentativa. Ver o cabeçalho.
    chave: novoId(),
    conteudo,
    privada,
    templateNome,
    templateValores,
    mencoes: privada ? mencoes : [],
    anexos,
    estado: 'pendente',
    erro: null,
    criadoEm: agora.toISOString(),
    aceitoEm: null,
  };
}

/**
 * A nova tentativa reusa tudo, inclusive a chave.
 *
 * Se o envio anterior chegou ao Chatwoot e só a resposta se perdeu, repetir com
 * a mesma chave devolve a resposta original em vez de enviar de novo — a
 * garantia da B30. Chave nova mandaria a mensagem duas vezes, e o cliente
 * receberia duas.
 */
export function repetirEnvio(envio: EnvioLocal): EnvioLocal {
  return { ...envio, estado: 'pendente', erro: null };
}

// ── O que a falha diz ─────────────────────────────────────────────────────

export interface Diagnostico {
  readonly motivo: string;
  /** O que fazer. Nunca promete reenvio automático onde não há (§8.2). */
  readonly acao: string;
  readonly podeRepetir: boolean;
  readonly correlationId: string | null;
}

/**
 * Ação recomendada por código do catálogo (B34).
 *
 * O catálogo dá título e `retryable`; o que fazer a seguir é decisão de
 * interface, e depende do erro. "Tente de novo" num `FORA_DA_JANELA` seria
 * mentira — o mesmo corpo vai ser recusado de novo até o cliente escrever.
 *
 * Nenhuma das duas falhas do motor afirma que a mensagem não saiu (#836): o
 * envio sem resposta pode ter chegado lá. O que se pode afirmar é que repetir
 * não duplica — a mesma `Idempotency-Key` vira o mesmo carimbo, e o motor
 * devolve a mensagem que já existe.
 */
const ACAO_POR_CODIGO: Record<string, string> = {
  ENGINE_INDISPONIVEL:
    'Tente de novo em alguns instantes. Se a mensagem já tiver saído, ela não sai duas vezes.',
  FORA_DA_JANELA:
    'A janela de atendimento fechou. Use um template aprovado, ou espere o cliente escrever de novo.',
  SEM_PERMISSAO:
    'Seu papel não permite enviar nesta caixa. Fale com o administrador da organização.',
  NAO_ENCONTRADO: 'Esta conversa não está mais disponível para você. Recarregue a lista.',
  REQUISICAO_INVALIDA: 'O conteúdo foi recusado. Revise o texto e envie de novo.',
  LIMITE_EXCEDIDO: 'Muitos envios em pouco tempo. Espere alguns segundos e tente de novo.',
  IDEMPOTENCIA_EM_CURSO:
    'Este envio ainda está sendo processado. Espere um instante antes de tentar de novo.',
  IDEMPOTENCIA_DIVERGENTE:
    'O texto mudou depois da primeira tentativa. Escreva a mensagem de novo.',
  // No envio, hoje, só a cota de armazenamento cheia (#717): o anexo não cabe,
  // e o motivo diz o que fazer. O texto sozinho nunca para.
  CONFLITO_DE_ESTADO: 'A mensagem não saiu. Sem o anexo, o texto pode ser enviado agora.',
};

export function diagnosticarFalha(erro: unknown): Diagnostico {
  if (erro instanceof ErroDeRede) {
    return {
      motivo: 'Não foi possível falar com o servidor.',
      // A conexão pode ter caído DEPOIS de o pedido chegar, com a mensagem já
      // enviada: não dá para afirmar que não saiu. Repetir é seguro, porque a
      // mesma chave não vira uma segunda mensagem (#836).
      acao: 'Confira a conexão e tente de novo. Se a mensagem já tiver saído, ela não sai duas vezes.',
      podeRepetir: true,
      correlationId: null,
    };
  }

  if (!(erro instanceof ErroDaApi)) {
    return {
      motivo: 'Algo deu errado antes de o envio sair.',
      acao: 'Recarregue a tela e tente de novo.',
      podeRepetir: false,
      correlationId: null,
    };
  }

  return {
    motivo: erro.problema.detail ?? erro.problema.title,
    acao:
      ACAO_POR_CODIGO[erro.codigo] ??
      (erro.retryable
        ? 'Tente de novo em alguns instantes.'
        : // Sem ação conhecida E sem retry: prometer qualquer coisa seria
          // inventar. O código de suporte é o que resolve de verdade.
          'Passe o código abaixo ao suporte para descobrir o motivo.'),
    podeRepetir: erro.retryable,
    correlationId: erro.correlationId,
  };
}

// ── Pareamento com a projeção ─────────────────────────────────────────────

/**
 * Tolerância entre o carimbo da resposta e o da projeção.
 *
 * O `criadaEm` que a API devolve e o `enviada_em` que a projeção traz vêm de
 * relógios diferentes — o nosso e o do Chatwoot. Um minuto cobre a diferença
 * sem alcançar uma mensagem de outro momento.
 */
export const TOLERANCIA_DE_PAREAMENTO_MS = 60_000;

export interface MensagemProjetada {
  readonly direcao: 'entrada' | 'saida' | 'sistema';
  readonly visibilidade: 'publica' | 'interna';
  readonly conteudo: string | null;
  readonly enviada_em: string;
}

/**
 * Este envio já apareceu na projeção?
 *
 * ── Por que por CONTEÚDO, e não por id ──────────────────────────────────
 *
 * Não há id em comum. A resposta do envio devolve o canônico da mensagem
 * (§3.2); a projeção não cunha id por mensagem — a decisão da C19 é que a
 * referência externa é a identidade dela. Os dois lados não se conhecem.
 *
 * Então o pareamento é por conteúdo, direção, visibilidade e proximidade de
 * tempo. É aproximado, e o erro possível é conhecido: duas mensagens idênticas
 * enviadas dentro do mesmo minuto pareiam com a primeira projetada, e o
 * otimista some um pouco antes do que deveria.
 *
 * A consequência disso é nenhuma — as duas mensagens saíram, as duas aparecem,
 * e o que muda é qual delas é a "local". O caminho alternativo seria a API
 * devolver a referência externa no envio, e isso é mudança de contrato: fica
 * registrado como a melhoria, não como defeito a contornar.
 */
export function jaProjetado(envio: EnvioLocal, mensagens: readonly MensagemProjetada[]): boolean {
  if (envio.estado !== 'aceita') return false;

  const referencia = Date.parse(envio.aceitoEm ?? envio.criadoEm);
  const visibilidade = envio.privada ? 'interna' : 'publica';

  return mensagens.some((mensagem) => {
    if (mensagem.direcao !== 'saida') return false;
    if (mensagem.visibilidade !== visibilidade) return false;
    if ((mensagem.conteudo ?? '') !== envio.conteudo) return false;

    const quando = Date.parse(mensagem.enviada_em);
    if (Number.isNaN(quando) || Number.isNaN(referencia)) return false;

    return Math.abs(quando - referencia) <= TOLERANCIA_DE_PAREAMENTO_MS;
  });
}

/**
 * Os envios que ainda precisam aparecer na tela por conta própria.
 *
 * O recorte por conversa vem PRIMEIRO, e não é redundância com quem chama: o
 * pareamento por conteúdo só esconde um envio quando ele aparece na projeção
 * desta conversa. Sem o recorte, um envio de outra conversa nunca pareia e
 * fica desenhado para sempre — que foi o defeito de 02/09/2026.
 */
export function enviosPendentesDeExibir(
  envios: readonly EnvioLocal[],
  mensagens: readonly MensagemProjetada[],
  conversaId: string | null,
): readonly EnvioLocal[] {
  return envios.filter(
    (envio) => envio.conversaId === conversaId && !jaProjetado(envio, mensagens),
  );
}

/**
 * Várias fotos de uma vez viram várias MENSAGENS.
 *
 * ── Onde o anexo se perdia ────────────────────────────────────────────────
 *
 * A tela mandava os três arquivos, a API recebia os três e o adapter os punha
 * em `attachments[]`. O motor guardava os três. E o cliente recebia UM.
 *
 * O corte é do provedor do WhatsApp dentro do motor, e está escrito na cara
 * (`whatsapp_cloud_service.rb`, v4.16.2):
 *
 *     def send_attachment_message(phone_number, message)
 *       attachment = message.attachments.first
 *
 * `.first`. Os outros ficam na conversa do Chatwoot e nunca saem — sem erro em
 * lugar nenhum, que é o pior tipo de perda: nossa tela mostrava três, o
 * vendedor via três, e o cliente recebia um.
 *
 * ── Por que dividir, e não "consertar o envio" ────────────────────────────
 *
 * Porque não há o que consertar do nosso lado: a API do WhatsApp Cloud aceita
 * UMA mídia por mensagem. O aplicativo do WhatsApp faz exatamente isto — cada
 * foto de uma seleção vira uma mensagem, exibidas juntas porque chegam
 * seguidas.
 *
 * ── A legenda vai na PRIMEIRA ─────────────────────────────────────────────
 *
 * É o que o WhatsApp faz e o que a pessoa espera: ela escreveu uma frase sobre
 * o conjunto. Repeti-la em cada foto mandaria a mesma frase três vezes ao
 * cliente; deixá-la sozinha numa quarta mensagem a separaria das fotos.
 *
 * ── A nota interna NÃO se divide ──────────────────────────────────────────
 *
 * Ela não passa pelo canal: fica no motor, que guarda os anexos todos sem
 * problema. Dividir transformaria uma anotação com três fotos em três
 * anotações, e o histórico da equipe é justamente onde o agrupamento importa.
 */
export interface ParteDoEnvio {
  readonly texto: string;
  readonly templateNome: string | undefined;
  readonly arquivos: readonly File[];
}

export function dividirEnvio(
  texto: string,
  privada: boolean,
  templateNome: string | undefined,
  arquivos: readonly File[],
): readonly ParteDoEnvio[] {
  // Nota interna, um anexo só, ou nenhum: uma mensagem, como sempre foi.
  if (privada || arquivos.length <= 1) {
    return [{ texto, templateNome, arquivos }];
  }

  return arquivos.map((arquivo, indice) => ({
    // A legenda e o template vão na primeira — ver o cabeçalho.
    texto: indice === 0 ? texto : '',
    templateNome: indice === 0 ? templateNome : undefined,
    arquivos: [arquivo],
  }));
}
